import { useCallback, useRef } from 'react';
import { useUnmount } from 'react-use';
/**
 * Represents a hook that uses the intersection observer to register
 * intersections with the viewport or a container element.
 *
 * @example
 *   const { observeElementRef } = useIntersectionObserver({
 *     handleIntersection: (entry) => {},
 *   });
 *
 *   <div ref={observeElementRef} />;
 */
export const useIntersectionObserver = ({ options, onIntersection, }) => {
    const handleIntersection = useCallback((entries) => entries.forEach(onIntersection), [onIntersection]);
    const intersectionObserver = useRef(new IntersectionObserver(handleIntersection, options));
    useUnmount(() => intersectionObserver.current.disconnect());
    const handleObserveElementRef = useCallback((element) => {
        if (element) {
            intersectionObserver.current.observe(element);
        }
    }, []);
    const handleObserveElement = useCallback((element) => {
        intersectionObserver.current.observe(element);
    }, []);
    return {
        observeElementRef: handleObserveElementRef,
        observeElement: handleObserveElement,
    };
};
